import { authedFetch, type SessionState } from './session.js';
import { diaSemana as diaSemanaApp } from './fechas.js';

/**
 * LA PESTAÑA «SEGUIMIENTO» (solo donde el servidor la enciende: hoy, el inquilino fluxo). El
 * servidor prepara todo (apps/toolkit-server/src/routes/seguimiento.ts y
 * seguimiento/secuencia-vistas.ts): la pantalla lee y pulsa botones. No decide a quién se
 * escribe ni qué etapa toca, y no escribe en GHL por su cuenta.
 *
 *   GET /api/seguimiento/vista       Enviados (también lo que salió de hola@ fuera del panel) con su número,
 *                                    y los avisos. El servidor manda además borradores y WhatsApp: desde el
 *                                    20-oct (redacción del panel aparcada, la escribe la skill) la pantalla no
 *                                    los enseña ni los lee.
 *   GET /api/seguimiento/secuencia   la vista «Secuencia» (15-oct; la primera desde el 20-oct): cifras,
 *                                    personas por ciclo y una fila por persona.
 *
 * Revamp del 6-oct (noche): las llamadas por hacer van al dialer de GHL; las derivaciones se procesan
 * solas y se informan como avisos de una línea. 20-oct, Joan: «¿podemos hacer el panel full visual y
 * quitar lo que ya no sirve?»: fuera Borradores y WhatsApp, con sus botones y sus rutas de cliente.
 *
 * Cada cosa trae su `origen`: 'lista' (la lista del día, con sus rutas de siempre en
 * /api/seguimiento/hoy/:id/...) o 'secuencia' (los toques y las secuencias). Con
 * `{ encendido: false }` la pestaña no existe y el inquilino ve su panel de siempre.
 */

export type PedirSeguimiento = (ruta: string, init: RequestInit) => Promise<Response>;
export type Origen = 'lista' | 'secuencia';
export interface Ciclo { n: number; de: number }
export type EstadoAccion = 'pendiente' | 'aplicando' | 'aplicada' | 'fallida' | 'incierta' | 'bloqueada';
export type EstadoLista = 'lista' | 'preparando' | 'fallida' | 'sin_lista';

/** Enviados mezcla lo del panel con lo que salió de hola@ fuera de él ('fuera': la skill, Gmail). */
export type OrigenEnviado = Origen | 'fuera';
/**
 * Cómo salió: correo 'auto' (solo), 'aprobado' (con tu clic) o 'fuera' (de hola@, fuera del panel);
 * WhatsApp 'whatsapp' (lo marcaste tú) o 'visto' (lo vio GHL: el que abriste sin marcar, o uno de fuera).
 */
export type ViaEnviado = 'auto' | 'aprobado' | 'whatsapp' | 'visto' | 'fuera';
export interface AdjuntoCorreo { nombre: string; url: string | null }
export interface DetalleCorreo {
  estado: 'ok' | 'sin_mensaje' | 'no_leido';
  asunto: string | null;
  texto: string | null;
  para: string | null;
  adjuntos: AdjuntoCorreo[];
  /** GHL no dijo si llevaba adjuntos (los correos de Gmail sincronizados no traen el dato). */
  adjuntosSinDato?: boolean;
}
export interface ItemEnviado {
  origen: OrigenEnviado;
  id: string;
  secuenciaId: string | null;
  nombre: string;
  empresa: string | null;
  canal: 'email' | 'whatsapp';
  via: ViaEnviado;
  asunto: string | null;
  texto: string | null;
  en: string;
  estado: 'enviado' | 'incierto' | 'rebotado';
  /** El motivo en una o dos palabras («Referido», «Tras la llamada»); null sin un motivo que decir. */
  chip: string | null;
  ciclo: Ciclo | null;
  meContesto: boolean;
  nota: string | null;
  ficha: string | null;
}

/**
 * Lo que se puede pulsar en «Por confirmar» (solo con las acciones solas apagadas: aplicar una
 * propuesta o dar de baja). Una llamada en duda ya no se pregunta (7-oct): la decide el servidor.
 */
export interface AccionAviso { tipo: 'aplicar' | 'baja'; origen: Origen; id: string; boton: string; estado: EstadoAccion; nota: string | null }

export interface Aviso {
  clave: string;
  texto: string;
  ficha: string | null;
  accion: AccionAviso | null;
}

/**
 * Los grupos de avisos. Arriba, a la vista, solo lo que pide a Joan algo (confirmar) o lo que fallo
 * (GRUPOS_PENDIENTES); lo que es solo para informar (GRUPOS_INFO) va plegado en una linea
 * (revision del 7-oct: con un aviso por grupo, el bloque pesaba mas que los borradores). «Te
 * llamaron» ya no existe: es una llamada por hacer y las llamadas no se ven aqui.
 */
export const GRUPOS_PENDIENTES = ['porConfirmar', 'noTerminado', 'llamadas'] as const;
export const GRUPOS_INFO = ['derivaciones', 'sinDatos', 'hechoSolo'] as const;
export const GRUPOS_AVISOS = [...GRUPOS_PENDIENTES, ...GRUPOS_INFO] as const;
export type GrupoAviso = typeof GRUPOS_AVISOS[number];
export type Avisos = Record<GrupoAviso, Aviso[]>;

export interface Cuentas { enviados: number; avisos: number }

export type VistaSeguimiento =
  | { encendido: false }
  | {
    encendido: true;
    fecha: string;
    estadoLista: EstadoLista;
    cuentas: Cuentas;
    enviados: ItemEnviado[];
    avisos: Avisos;
  };

/* ------------------------------------------------------------------------- *
 * Lo que se acepta del servidor
 * ------------------------------------------------------------------------- */

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;
const ESTADOS_LISTA = new Set(['lista', 'preparando', 'fallida', 'sin_lista']);
const ESTADOS_ACCION = new Set(['pendiente', 'aplicando', 'aplicada', 'fallida', 'incierta', 'bloqueada']);
/** Solo la ficha de un contacto en GHL: nada de otra web. */
const FICHA_RE = /^https:\/\/app\.gohighlevel\.com\/v2\/location\/[A-Za-z0-9_-]{1,64}\/contacts\/detail\/[A-Za-z0-9_-]{1,64}$/;

const texto = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const objeto = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null);
const instante = (v: unknown): string | null => (typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null);
const fecha = (v: unknown): string | null => (typeof v === 'string' && FECHA_RE.test(v) ? v : null);
const cuenta = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0);
const origenDe = (v: unknown): Origen | null => (v === 'lista' || v === 'secuencia' ? v : null);
const fichaDe = (v: unknown): string | null => (typeof v === 'string' && FICHA_RE.test(v) ? v : null);
const lista = <T,>(v: unknown, f: (x: unknown) => T | null): T[] => (Array.isArray(v) ? v.map(f).filter((x): x is T => x !== null) : []);
const chipDe = (v: unknown): string | null => { const t = texto(v); return t && t.length <= 24 ? t : null; };

function cicloDe(v: unknown): Ciclo | null {
  const r = objeto(v);
  const n = cuenta(r?.n); const de = cuenta(r?.de);
  return r && n >= 1 && de >= n && de <= 12 ? { n, de } : null;
}

/** Lo comun a todo: de donde viene, su id y a quien. null si falta algo de eso. */
function base(r: Record<string, unknown> | null): { origen: Origen; id: string; secuenciaId: string | null; nombre: string; empresa: string | null; ficha: string | null } | null {
  if (!r) return null;
  const origen = origenDe(r.origen); const id = texto(r.id); const nombre = texto(r.nombre);
  if (!origen || !id || !nombre) return null;
  return { origen, id, secuenciaId: texto(r.secuenciaId), nombre, empresa: texto(r.empresa), ficha: fichaDe(r.ficha) };
}

const VIAS: ReadonlySet<string> = new Set(['auto', 'aprobado', 'whatsapp', 'visto', 'fuera']);

export function itemEnviadoDesde(raw: unknown): ItemEnviado | null {
  const r = objeto(raw);
  // Lo de fuera del panel trae su propio origen: el resto de la forma es la de siempre.
  const fuera = r?.origen === 'fuera';
  const b = base(fuera ? { ...r, origen: 'lista' } : r);
  const en = instante(r?.en);
  if (!r || !b || !en) return null;
  if (r.canal !== 'email' && r.canal !== 'whatsapp') return null;
  if (typeof r.via !== 'string' || !VIAS.has(r.via)) return null;
  if (r.estado !== 'enviado' && r.estado !== 'incierto' && r.estado !== 'rebotado') return null;
  return {
    ...b, origen: fuera ? 'fuera' : b.origen, secuenciaId: fuera ? null : b.secuenciaId, canal: r.canal, via: r.via as ViaEnviado, asunto: texto(r.asunto), texto: texto(r.texto), en,
    estado: r.estado, chip: chipDe(r.chip), ciclo: fuera ? null : cicloDe(r.ciclo), meContesto: !fuera && b.origen === 'secuencia' && !!b.secuenciaId && r.meContesto === true, nota: texto(r.nota),
  };
}

function accionAvisoDesde(v: unknown): AccionAviso | null {
  const r = objeto(v);
  if (!r) return null;
  if (r.tipo !== 'aplicar' && r.tipo !== 'baja') return null;
  const origen = origenDe(r.origen); const id = texto(r.id);
  if (!origen || !id || typeof r.estado !== 'string' || !ESTADOS_ACCION.has(r.estado)) return null;
  const boton = texto(r.boton);
  return { tipo: r.tipo, origen, id, boton: boton && boton.length <= 40 ? boton : r.tipo === 'baja' ? 'Dar de baja' : 'Aplicar', estado: r.estado as EstadoAccion, nota: texto(r.nota) };
}

export function avisoDesde(raw: unknown): Aviso | null {
  const r = objeto(raw);
  const clave = texto(r?.clave); const t = texto(r?.texto);
  if (!r || !clave || !t) return null;
  return { clave, texto: t, ficha: fichaDe(r.ficha), accion: accionAvisoDesde(r.accion) };
}

/** Los avisos por grupo; un grupo que falta o una cosa sin la forma esperada, fuera. */
export function avisosDesde(raw: unknown): Avisos {
  const r = objeto(raw);
  return Object.fromEntries(GRUPOS_AVISOS.map((g) => [g, lista(r?.[g], avisoDesde)])) as Avisos;
}

/** La respuesta de GET /api/seguimiento/vista, o null si no tiene la forma esperada. */
export function vistaDesde(raw: unknown): VistaSeguimiento | null {
  const r = objeto(raw);
  if (!r) return null;
  if (r.encendido === false) return { encendido: false };
  if (r.encendido !== true || !fecha(r.fecha)) return null;
  const estadoLista = typeof r.estadoLista === 'string' && ESTADOS_LISTA.has(r.estadoLista) ? r.estadoLista as EstadoLista : 'sin_lista';
  const c = objeto(r.cuentas);
  return {
    encendido: true, fecha: r.fecha as string, estadoLista,
    cuentas: { enviados: cuenta(c?.enviados), avisos: cuenta(c?.avisos) },
    enviados: lista(r.enviados, itemEnviadoDesde),
    avisos: avisosDesde(r.avisos),
  };
}

/* ------------------------------------------------------------------------- *
 * La vista «Secuencia» (15-oct; el servidor la arma en secuencia-panel.ts)
 * ------------------------------------------------------------------------- */

export interface ToquesHechos { llamada: number; email: number; whatsapp: number }
export type CanalToque = 'llamada' | 'email' | 'whatsapp';
/** Los días naturales que dura la secuencia con cada llamada a tiempo (DIAS_PLAZO_SECUENCIA en S/seguimiento/secuencia-reglas.ts). */
export const DIAS_PLAZO_SECUENCIA = 21;

export interface FilaSecuencia {
  secuenciaId: string;
  nombre: string;
  empresa: string | null;
  estado: 'dentro' | 'salio' | 'terminada';
  /** El ciclo en el que está. */
  ciclo: Ciclo;
  /** Ciclos completos (0 a 8) y su porcentaje. */
  hechos: number;
  porcentaje: number;
  toques: ToquesHechos;
  diasSecuencia: number;
  /** Dentro y con más de DIAS_PLAZO_SECUENCIA días en la secuencia (false si el servidor no lo manda). */
  fueraDePlazo: boolean;
  diasDeal: number | null;
  /** Solo si espera tu llamada. `retraso`: lleva 2 días hábiles o más. */
  espera: { desde: string; dias: number; retraso: boolean } | null;
  ultimo: { canal: CanalToque; texto: string; en: string } | null;
  porEnviar: Array<'email' | 'whatsapp'>;
  salida: { texto: string; en: string | null } | null;
  etapa: string | null;
  ficha: string | null;
}

export interface PanelSecuencia {
  fecha: string;
  ventanaDias: number;
  cifras: {
    enSecuencia: number;
    entraron: number;
    esperan: { n: number; conRetraso: number; masAntigua: number | null };
    /** Las de dentro con más de DIAS_PLAZO_SECUENCIA días; null si el servidor no lo manda (uno de antes del 20-oct). */
    fueraDePlazo: number | null;
    toques: ToquesHechos;
    respondieron: { n: number; porCanal: ToquesHechos };
  };
  porCiclo: Array<{ ciclo: number; esperan: number; enCurso: number }>;
  filas: FilaSecuencia[];
}

const CANALES_TOQUE = new Set(['llamada', 'email', 'whatsapp']);
const toquesDe = (v: unknown): ToquesHechos => { const r = objeto(v); return { llamada: cuenta(r?.llamada), email: cuenta(r?.email), whatsapp: cuenta(r?.whatsapp) }; };
const enRango = (v: unknown, max: number): number => Math.min(cuenta(v), max);
/** Un texto corto del servidor («Llamada: No contesta», «Respondió por correo en el ciclo 2»). */
const frase = (v: unknown): string | null => { const t = texto(v); return t && t.length <= 120 ? t : null; };

export function filaSecuenciaDesde(raw: unknown): FilaSecuencia | null {
  const r = objeto(raw);
  const secuenciaId = texto(r?.secuenciaId); const nombre = texto(r?.nombre); const ciclo = cicloDe(r?.ciclo);
  if (!r || !secuenciaId || !nombre || !ciclo) return null;
  if (r.estado !== 'dentro' && r.estado !== 'salio' && r.estado !== 'terminada') return null;
  const e = objeto(r.espera); const desde = fecha(e?.desde);
  const u = objeto(r.ultimo); const uTexto = frase(u?.texto); const uEn = instante(u?.en);
  const sa = objeto(r.salida); const saTexto = frase(sa?.texto);
  return {
    secuenciaId, nombre, empresa: texto(r.empresa), estado: r.estado, ciclo,
    hechos: enRango(r.hechos, ciclo.de), porcentaje: enRango(r.porcentaje, 100), toques: toquesDe(r.toques),
    diasSecuencia: cuenta(r.diasSecuencia), fueraDePlazo: r.fueraDePlazo === true && r.estado === 'dentro', diasDeal: typeof r.diasDeal === 'number' ? cuenta(r.diasDeal) : null,
    espera: e && desde ? { desde, dias: cuenta(e.dias), retraso: e.retraso === true } : null,
    ultimo: u && uTexto && uEn && typeof u.canal === 'string' && CANALES_TOQUE.has(u.canal) ? { canal: u.canal as CanalToque, texto: uTexto, en: uEn } : null,
    porEnviar: lista(r.porEnviar, (x) => (x === 'email' || x === 'whatsapp' ? x : null)),
    salida: sa && saTexto ? { texto: saTexto, en: instante(sa.en) } : null,
    etapa: chipDe(r.etapa), ficha: fichaDe(r.ficha),
  };
}

/** La respuesta de GET /api/seguimiento/secuencia; 'apagado' sin el módulo; null si no tiene la forma esperada. */
export function panelSecuenciaDesde(raw: unknown): PanelSecuencia | 'apagado' | null {
  const r = objeto(raw);
  if (!r) return null;
  if (r.encendido === false) return 'apagado';
  if (r.encendido !== true || !fecha(r.fecha)) return null;
  const c = objeto(r.cifras); const es = objeto(c?.esperan); const re = objeto(c?.respondieron);
  return {
    fecha: r.fecha as string, ventanaDias: cuenta(r.ventanaDias),
    cifras: {
      enSecuencia: cuenta(c?.enSecuencia), entraron: cuenta(c?.entraron),
      esperan: { n: cuenta(es?.n), conRetraso: cuenta(es?.conRetraso), masAntigua: typeof es?.masAntigua === 'number' ? cuenta(es.masAntigua) : null },
      fueraDePlazo: typeof c?.fueraDePlazo === 'number' ? cuenta(c.fueraDePlazo) : null,
      toques: toquesDe(c?.toques),
      respondieron: { n: cuenta(re?.n), porCanal: toquesDe(re?.porCanal) },
    },
    porCiclo: lista(r.porCiclo, (x) => {
      const o = objeto(x); const ciclo = cuenta(o?.ciclo);
      return o && ciclo >= 1 && ciclo <= 12 ? { ciclo, esperan: cuenta(o.esperan), enCurso: cuenta(o.enCurso) } : null;
    }),
    filas: lista(r.filas, filaSecuenciaDesde),
  };
}

export async function leerPanelSecuencia(pedir: PedirSeguimiento): Promise<PanelSecuencia | 'apagado'> {
  const r = await pedir('/api/seguimiento/secuencia', { method: 'GET', cache: 'no-store' });
  if (!r.ok) throw new Error(`secuencia_${r.status}`);
  const v = panelSecuenciaDesde(await r.json().catch(() => null));
  if (!v) throw new Error('secuencia_respuesta_no_valida');
  return v;
}

export async function leerVista(pedir: PedirSeguimiento): Promise<VistaSeguimiento> {
  const r = await pedir('/api/seguimiento/vista', { method: 'GET', cache: 'no-store' });
  if (!r.ok) throw new Error(`seguimiento_${r.status}`);
  const v = vistaDesde(await r.json().catch(() => null));
  if (!v) throw new Error('seguimiento_respuesta_no_valida');
  return v;
}

/** Lee el mensaje real del proveedor al abrir un correo del historial (también uno de fuera del panel). Solo se pinta texto plano. */
export async function leerDetalleCorreo(pedir: PedirSeguimiento, x: { origen: OrigenEnviado; id: string }): Promise<DetalleCorreo> {
  const r = await pedir(`/api/seguimiento/mensajes/${x.origen}/${encodeURIComponent(x.id)}`, { method: 'GET', cache: 'no-store' });
  if (!r.ok) throw new Error(`seguimiento_correo_${r.status}`);
  const d = objeto(await r.json().catch(() => null));
  if (!d || (d.estado !== 'ok' && d.estado !== 'sin_mensaje' && d.estado !== 'no_leido')) throw new Error('seguimiento_correo_no_valido');
  if (d.estado === 'ok' && !Array.isArray(d.adjuntos)) throw new Error('seguimiento_adjuntos_no_leidos');
  const adjuntos = lista(d.adjuntos, (v): AdjuntoCorreo | null => {
    const a = objeto(v); const nombre = texto(a?.nombre);
    if (!a || !nombre) return null;
    let url: string | null = null;
    if (typeof a.url === 'string') {
      try {
        const u = new URL(a.url);
        if ((u.protocol === 'https:' || u.protocol === 'http:') && !u.username && !u.password) url = u.href;
      } catch { /* El nombre se conserva aunque no haya un enlace válido. */ }
    }
    return { nombre, url };
  });
  return { estado: d.estado, asunto: texto(d.asunto), texto: texto(d.texto), para: texto(d.para), adjuntos, ...(d.adjuntosSinDato === true ? { adjuntosSinDato: true } : {}) };
}

/* ------------------------------------------------------------------------- *
 * Los botones
 * ------------------------------------------------------------------------- */

/**
 * Lo que contesta el servidor a un boton: hecho (con lo que devuelva), ya no existe, el modulo
 * se apago, o un rechazo con su razon (400, 409 o 503: no se hizo nada). Un 5xx o la red
 * caida lanzan: no se sabe que paso, y quien llama vuelve a leer.
 */
export type Resultado = { hecho: Record<string, unknown> } | 'no_existe' | 'apagado' | { rechazo: string; detalle: string | null };

async function pedirAccion(pedir: PedirSeguimiento, ruta: string, method: 'POST' | 'DELETE', cuerpo?: unknown): Promise<Resultado> {
  const init: RequestInit = { method };
  if (cuerpo !== undefined) { init.headers = { 'Content-Type': 'application/json' }; init.body = JSON.stringify(cuerpo); }
  const r = await pedir(ruta, init);
  const data = objeto(await r.json().catch(() => null));
  if (r.status === 404) return 'no_existe';
  if (r.status === 400 || r.status === 409 || r.status === 503) return { rechazo: texto(data?.error) ?? `error_${r.status}`, detalle: texto(data?.detail) };
  if (!r.ok) throw new Error(`seguimiento_${r.status}`);
  if (data?.encendido === false) return 'apagado';
  return { hecho: data ?? {} };
}

const RAIZ = '/api/seguimiento';
const de = (id: string) => encodeURIComponent(id);
export interface Ref { origen: Origen; id: string }

/** «Me contestó»: sale de la secuencia (por si GHL no vio su respuesta). */
export const meContesto = (pedir: PedirSeguimiento, secuenciaId: string, canal?: 'email' | 'whatsapp' | 'llamada' | 'sms') =>
  pedirAccion(pedir, `${RAIZ}/secuencias/${de(secuenciaId)}/contesto`, 'POST', canal ? { canal } : {});

/** «Aplicar» la propuesta: una escritura verificada en GHL (etapa o tarea). */
export const aplicarPropuesta = (pedir: PedirSeguimiento, r: Ref) =>
  pedirAccion(pedir, r.origen === 'lista' ? `${RAIZ}/hoy/${de(r.id)}/aplicar` : `${RAIZ}/secuencias/${de(r.id)}/aplicar`, 'POST');

/** «Dar de baja» (de la lista o de la secuencia): DND y no_llamar, verificado en GHL. */
export const darDeBaja = (pedir: PedirSeguimiento, r: Ref) =>
  pedirAccion(pedir, r.origen === 'lista' ? `${RAIZ}/hoy/${de(r.id)}/baja` : `${RAIZ}/secuencias/${de(r.id)}/baja`, 'POST');

export function pedirConSesion(session: SessionState): PedirSeguimiento {
  return (ruta, init) => authedFetch(session, ruta, init);
}

/* ------------------------------------------------------------------------- *
 * Fechas y frases
 * ------------------------------------------------------------------------- */

const MADRID = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
function enMadrid(d: Date): { fecha: string; hora: number; minuto: number } {
  const p = Object.fromEntries(MADRID.formatToParts(d).map((x) => [x.type, x.value]));
  return { fecha: `${p.year}-${p.month}-${p.day}`, hora: Number(p.hour), minuto: Number(p.minute) };
}

const menosUnDia = (f: string): string => { const d = new Date(`${f}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); };

/** El día de Madrid de un instante (AAAA-MM-DD). */
export const diaDeMadrid = (iso: string): string => enMadrid(new Date(iso)).fecha;

/** «Hoy», «Ayer» o «lun 5 oct»: el encabezado de un día en Enviados. */
export function fraseGrupoDia(f: string, ahora: Date): string {
  const hoy = enMadrid(ahora).fecha;
  if (f === hoy) return 'Hoy';
  if (f === menosUnDia(hoy)) return 'Ayer';
  return diaSemanaApp(f);
}

/** «hoy», «ayer» o «jue 8 oct»: cuándo fue algo, dentro de una frase. */
export function fraseDiaRelativo(iso: string, ahora: Date): string {
  const f = enMadrid(new Date(iso)).fecha;
  const hoy = enMadrid(ahora).fecha;
  if (f === hoy) return 'hoy';
  if (f === menosUnDia(hoy)) return 'ayer';
  return diaSemanaApp(f);
}

/** «10:32» (la hora de Madrid de un instante). */
export function fraseHora(iso: string): string {
  const m = enMadrid(new Date(iso));
  return `${m.hora}:${String(m.minuto).padStart(2, '0')}`;
}
